/**
 * Sales Pipeline Sync — read-only GHL late-stage opportunity board inside HQ.
 *
 * Shows ONLY the two configured stages (Proposal Follow Up + Agreement
 * Sent/Signed) from the "Sales 3" pipeline. Admin can trigger sync, view logs,
 * and inspect raw payloads. Everyone else sees a curated read-only board.
 */
import { useEffect, useMemo, useState } from "react";
import { apiClient } from "../App";
import { toast } from "sonner";
import {
  Search, RefreshCw, Database, Mail, Phone, Calendar, ChevronRight,
  AlertTriangle, Inbox, Settings2, ArrowUpDown, ExternalLink, CheckCircle2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from "../components/ui/sheet";

const fmtCurrency = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const fmtRelative = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const diff = Date.now() - d.getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.round(h / 24);
  if (days < 14) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};
const initials = (name) => (name || "?").split(" ").filter(Boolean).slice(0, 2).map(s => s[0]?.toUpperCase()).join("");

const STAGE_DEFS = [
  { key: "proposal_follow_up",      label: "Proposal Follow Up",     accent: "blue"   },
  { key: "agreement_sent_signed",   label: "Agreement Sent/Signed",  accent: "emerald"},
];

export default function SalesPipelineSync() {
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);

  // Filters
  const [q, setQ] = useState("");
  const [assigned, setAssigned] = useState("__all__");
  const [source, setSource] = useState("__all__");
  const [minValue, setMinValue] = useState("");
  const [maxValue, setMaxValue] = useState("");
  const [sort, setSort] = useState("updated_desc");

  const isAdmin = useMemo(() => ["COO", "Admin", "Finance"].includes(me?.role), [me]);

  // Load
  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [oppRes, statsRes] = await Promise.all([
        apiClient.get("/ghl-pipeline/opportunities", { params: { q, sort } }),
        apiClient.get("/ghl-pipeline/stats"),
      ]);
      setOpps(oppRes.data?.opportunities || []);
      setStats(statsRes.data || null);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    apiClient.get("/auth/me").then(r => setMe(r.data)).catch(() => setMe(null));
  }, []);

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [sort]);
  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => loadAll(), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  // Client-side filtering (assigned, source, value range)
  const filtered = useMemo(() => {
    return opps.filter(o => {
      if (assigned !== "__all__" && (o.assigned_user_id || "__unassigned__") !== assigned) return false;
      if (source !== "__all__" && (o.source || "__none__") !== source) return false;
      const v = Number(o.opportunity_value || 0);
      if (minValue !== "" && v < Number(minValue)) return false;
      if (maxValue !== "" && v > Number(maxValue)) return false;
      return true;
    });
  }, [opps, assigned, source, minValue, maxValue]);

  const byStage = useMemo(() => {
    const out = { proposal_follow_up: [], agreement_sent_signed: [] };
    filtered.forEach(o => { if (out[o.internal_stage_key]) out[o.internal_stage_key].push(o); });
    return out;
  }, [filtered]);

  // Unique assignees / sources for filter dropdowns
  const assigneeOptions = useMemo(() => {
    const map = new Map();
    opps.forEach(o => {
      const id = o.assigned_user_id || "__unassigned__";
      const name = o.assigned_user_name || (id === "__unassigned__" ? "Unassigned in GHL" : id);
      if (!map.has(id)) map.set(id, name);
    });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [opps]);

  const sourceOptions = useMemo(() => {
    const set = new Set();
    opps.forEach(o => o.source && set.add(o.source));
    return Array.from(set);
  }, [opps]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await apiClient.post("/ghl-pipeline/sync");
      if (r.data?.error) {
        toast.error(`Sync failed: ${r.data.error.slice(0, 200)}`);
      } else {
        const log = r.data?.log || {};
        toast.success(`Sync complete — ${log.records_created || 0} created, ${log.records_updated || 0} updated`);
      }
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="ghl-pipeline-page">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-6 sm:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-7">
          <div>
            <h1 className="text-[22px] sm:text-[28px] font-light tracking-tight text-zinc-900">Sales Pipeline Sync</h1>
            <p className="text-[13px] text-zinc-500 mt-1">Live view of selected GHL pipeline stages inside HQ. Read-only.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {isAdmin && (
              <AdminDebugDrawer onSyncTriggered={loadAll} />
            )}
            {isAdmin && (
              <Button onClick={handleSync} disabled={syncing} className="h-9 gap-1.5 bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="ghl-sync-now">
                <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
                {syncing ? "Syncing…" : "Sync Now"}
              </Button>
            )}
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-7">
          <StatTile label="Total Visible" value={stats?.total_count ?? 0} loading={loading} />
          <StatTile
            label="Proposal Follow Up"
            value={stats?.by_stage?.proposal_follow_up?.count ?? 0}
            sub={fmtCurrency(stats?.by_stage?.proposal_follow_up?.total_value)}
            loading={loading}
            accent="blue"
          />
          <StatTile
            label="Agreement Sent/Signed"
            value={stats?.by_stage?.agreement_sent_signed?.count ?? 0}
            sub={fmtCurrency(stats?.by_stage?.agreement_sent_signed?.total_value)}
            loading={loading}
            accent="emerald"
          />
          <StatTile label="Total Pipeline Value" value={fmtCurrency(stats?.total_value)} loading={loading} numeric />
          <StatTile
            label="Last Sync"
            value={stats?.last_sync ? fmtRelative(stats.last_sync.sync_completed_at || stats.last_sync.sync_started_at) : "Never"}
            sub={stats?.last_sync?.status === "failed" ? "Failed" : stats?.last_sync ? "Success" : ""}
            loading={loading}
            danger={stats?.last_sync?.status === "failed"}
            small
          />
        </div>

        {/* Filters */}
        <div className="border border-zinc-200 rounded-xl bg-white p-3 mb-5 flex flex-wrap items-center gap-2.5">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" />
            <Input
              placeholder="Search by name, email, phone, or title…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-8 h-8 text-[12.5px] border-zinc-200"
              data-testid="ghl-search"
            />
          </div>
          <Select value={assigned} onValueChange={setAssigned}>
            <SelectTrigger className="h-8 w-[180px] text-[12px]" data-testid="ghl-filter-assignee"><SelectValue placeholder="Assignee" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All assignees</SelectItem>
              {assigneeOptions.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger className="h-8 w-[140px] text-[12px]"><SelectValue placeholder="Source" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All sources</SelectItem>
              {sourceOptions.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="number" placeholder="Min $" value={minValue} onChange={(e) => setMinValue(e.target.value)} className="h-8 w-[90px] text-[12px] border-zinc-200" />
          <Input type="number" placeholder="Max $" value={maxValue} onChange={(e) => setMaxValue(e.target.value)} className="h-8 w-[90px] text-[12px] border-zinc-200" />
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="h-8 w-[170px] text-[12px]" data-testid="ghl-sort">
              <div className="flex items-center gap-1.5"><ArrowUpDown className="w-3 h-3" /><SelectValue /></div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="value_desc">Highest value</SelectItem>
              <SelectItem value="value_asc">Lowest value</SelectItem>
              <SelectItem value="updated_desc">Newest updated</SelectItem>
              <SelectItem value="updated_asc">Oldest updated</SelectItem>
              <SelectItem value="appointment">Appointment date</SelectItem>
              <SelectItem value="stage">Stage</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Error state */}
        {error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 mb-5 flex items-start gap-2.5">
            <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
            <div className="text-[12.5px] text-rose-900">
              <strong>Failed to load.</strong> {error}
            </div>
          </div>
        )}

        {/* Kanban board */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="ghl-board">
          {STAGE_DEFS.map(stage => (
            <StageColumn
              key={stage.key}
              stage={stage}
              opportunities={byStage[stage.key] || []}
              loading={loading}
              isAdmin={isAdmin}
            />
          ))}
        </div>

        {/* Global empty state */}
        {!loading && !error && opps.length === 0 && (
          <div className="border border-dashed border-zinc-300 bg-zinc-50/40 rounded-xl px-8 py-14 text-center mt-5">
            <div className="w-10 h-10 rounded-lg bg-white border border-zinc-200 mx-auto flex items-center justify-center mb-3">
              <Inbox className="w-5 h-5 text-zinc-400" />
            </div>
            <div className="text-[13.5px] font-medium text-zinc-700">No synced opportunities yet</div>
            <p className="text-[12px] text-zinc-500 mt-1 max-w-md mx-auto leading-relaxed">
              {isAdmin
                ? <>Click <strong>Sync Now</strong> to import Proposal Follow Up and Agreement Sent/Signed opportunities from GHL.</>
                : "Ask an admin to run an initial sync to import opportunities from GHL."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Subcomponents ────────────────────────────────────────────────────────

function StatTile({ label, value, sub, loading, accent, danger, small, numeric }) {
  const accentClasses = {
    blue:    "before:bg-blue-500",
    emerald: "before:bg-emerald-500",
  }[accent] || "";
  return (
    <div className={`relative rounded-xl border border-zinc-200 bg-white px-4 py-3 overflow-hidden ${accent ? "before:absolute before:inset-y-0 before:left-0 before:w-[3px] " + accentClasses : ""}`}>
      <div className="text-[10px] uppercase tracking-[0.12em] text-zinc-500 font-medium">{label}</div>
      <div className={`mt-1 font-semibold tabular-nums tracking-tight ${small ? "text-[14px]" : numeric ? "text-[20px]" : "text-[22px]"} ${danger ? "text-rose-600" : "text-zinc-900"}`}>
        {loading ? <span className="inline-block w-12 h-5 bg-zinc-100 rounded animate-pulse" /> : value}
      </div>
      {sub && <div className={`text-[11px] mt-0.5 ${danger ? "text-rose-500" : "text-zinc-500"}`}>{sub}</div>}
    </div>
  );
}

function StageColumn({ stage, opportunities, loading, isAdmin }) {
  const total = opportunities.reduce((s, o) => s + Number(o.opportunity_value || 0), 0);
  const accent = stage.accent === "emerald" ? "bg-emerald-500" : "bg-blue-500";
  return (
    <div className="rounded-xl border border-zinc-200 bg-zinc-50/30 overflow-hidden flex flex-col" data-testid={`ghl-column-${stage.key}`}>
      <div className="px-4 py-3 border-b border-zinc-200 bg-white flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span className={`w-1.5 h-5 rounded-full ${accent}`} />
          <div>
            <div className="text-[13.5px] font-semibold tracking-tight text-zinc-900">{stage.label}</div>
            <div className="text-[10.5px] text-zinc-500 mt-0.5">{opportunities.length} opportunities · {fmtCurrency(total)}</div>
          </div>
        </div>
        <Badge variant="outline" className="text-[10px] font-medium bg-white border-zinc-200 text-zinc-600">
          <Database className="w-2.5 h-2.5 mr-1" /> Synced from GHL
        </Badge>
      </div>
      <div className="p-2.5 space-y-2 max-h-[1200px] overflow-y-auto">
        {loading && Array(3).fill(0).map((_, i) => (
          <div key={i} className="rounded-lg border border-zinc-200 bg-white px-3 py-3 animate-pulse">
            <div className="h-3 w-2/3 bg-zinc-100 rounded mb-2" />
            <div className="h-2 w-1/3 bg-zinc-100 rounded" />
          </div>
        ))}
        {!loading && opportunities.length === 0 && (
          <div className="text-center text-[11.5px] text-zinc-400 px-3 py-10 italic">
            No opportunities currently exist in this stage.
          </div>
        )}
        {!loading && opportunities.map(o => (
          <OpportunityCard key={o.ghl_opportunity_id} o={o} isAdmin={isAdmin} />
        ))}
      </div>
    </div>
  );
}

function OpportunityCard({ o, isAdmin }) {
  const [open, setOpen] = useState(false);
  const assignee = o.assigned_user_name || "Unassigned in GHL";
  const unassigned = !o.assigned_user_id;
  return (
    <div
      className="group rounded-lg border border-zinc-200 bg-white px-3 py-2.5 hover:border-zinc-300 hover:bg-white transition-colors cursor-default"
      data-testid={`ghl-opp-${o.ghl_opportunity_id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-medium text-zinc-900 truncate leading-tight">{o.opportunity_title || o.contact_name}</div>
          {o.contact_name && o.contact_name !== o.opportunity_title && (
            <div className="text-[11px] text-zinc-500 truncate mt-0.5">{o.contact_name}</div>
          )}
        </div>
        <div className="text-[13px] font-semibold tabular-nums text-zinc-900 shrink-0">{fmtCurrency(o.opportunity_value)}</div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10.5px] text-zinc-500">
        {o.contact_email && <span className="inline-flex items-center gap-0.5 truncate max-w-[180px]"><Mail className="w-2.5 h-2.5" />{o.contact_email}</span>}
        {o.contact_phone && <span className="inline-flex items-center gap-0.5"><Phone className="w-2.5 h-2.5" />{o.contact_phone}</span>}
        {o.appointment_at && <span className="inline-flex items-center gap-0.5 text-amber-700"><Calendar className="w-2.5 h-2.5" />{new Date(o.appointment_at).toLocaleDateString()}</span>}
      </div>

      <div className="mt-2 pt-2 border-t border-zinc-100 flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <div className={`w-5 h-5 rounded-full text-[9px] font-semibold flex items-center justify-center shrink-0 ${unassigned ? "bg-zinc-100 text-zinc-400" : "bg-zinc-900 text-white"}`}>
            {unassigned ? "?" : initials(assignee)}
          </div>
          <span className={`text-[10.5px] truncate ${unassigned ? "text-zinc-400 italic" : "text-zinc-600"}`}>{assignee}</span>
          {o.source && <><span className="text-zinc-300">·</span><span className="text-[10px] text-zinc-500">{o.source}</span></>}
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-zinc-400">{fmtRelative(o.date_updated)}</span>
          {isAdmin && (
            <button onClick={() => setOpen(true)} className="opacity-0 group-hover:opacity-100 transition-opacity text-zinc-400 hover:text-zinc-900" title="Raw payload">
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {open && <RawPayloadModal oppId={o.ghl_opportunity_id} onClose={() => setOpen(false)} />}
    </div>
  );
}

function RawPayloadModal({ oppId, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    apiClient.get(`/ghl-pipeline/opportunities/${oppId}/raw`)
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || e.message));
  }, [oppId]);
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
          <div className="text-[13px] font-semibold text-zinc-900">Raw GHL Payload</div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-900 text-xs">Close</button>
        </div>
        <pre className="p-4 text-[11px] overflow-auto flex-1 bg-zinc-50 font-mono leading-relaxed">
{err ? `Error: ${err}` : data ? JSON.stringify(data.raw_payload_json || data, null, 2) : "Loading…"}
        </pre>
      </div>
    </div>
  );
}

function AdminDebugDrawer({ onSyncTriggered }) {
  const [cfg, setCfg] = useState(null);
  const [logs, setLogs] = useState([]);
  const [discovery, setDiscovery] = useState(null);
  const [discovering, setDiscovering] = useState(false);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const [cRes, lRes] = await Promise.all([
        apiClient.get("/ghl-pipeline/config"),
        apiClient.get("/ghl-pipeline/sync-logs"),
      ]);
      setCfg(cRes.data);
      setLogs(lRes.data?.logs || []);
    } catch { /* admin-only — silent for now */ }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const handleDiscover = async () => {
    setDiscovering(true);
    try {
      const r = await apiClient.get("/ghl-pipeline/discover");
      setDiscovery(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Discovery failed — verify GHL_LOCATION_ID is set");
    } finally {
      setDiscovering(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 gap-1.5 border-zinc-200" data-testid="ghl-debug-drawer">
          <Settings2 className="w-3.5 h-3.5" /> Sync Settings &amp; Logs
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[520px] sm:max-w-[520px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="text-[16px] tracking-tight">Sync Settings &amp; Logs</SheetTitle>
        </SheetHeader>
        <div className="mt-5 space-y-5">
          {cfg && (
            <section>
              <div className="text-[10px] uppercase tracking-[0.12em] text-zinc-500 font-medium mb-2">Configuration</div>
              <div className="rounded-lg border border-zinc-200 divide-y divide-zinc-100 text-[12px]">
                <ConfigRow k="API Key" v={cfg.api_key_present ? "Configured" : "Missing"} ok={cfg.api_key_present} />
                <ConfigRow k="Location ID" v={cfg.location_id || "Not set"} ok={!!cfg.location_id} />
                <ConfigRow k="Pipeline ID" v={cfg.pipeline_id || `(resolve by name: ${cfg.pipeline_name})`} ok />
                <ConfigRow k="Pipeline Name" v={cfg.pipeline_name} ok />
                <ConfigRow k="Allowed Stages" v={cfg.allowed_stage_names.join(", ")} ok />
                <ConfigRow k="API Version" v={cfg.api_version} ok />
                <ConfigRow k="Enabled" v={cfg.enabled ? "Yes" : "No"} ok={cfg.enabled} />
              </div>
            </section>
          )}

          <section>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-[0.12em] text-zinc-500 font-medium">Discover Pipelines</div>
              <Button size="sm" variant="outline" onClick={handleDiscover} disabled={discovering} className="h-7 text-[11px] border-zinc-200">
                {discovering ? "Loading…" : "Fetch from GHL"}
              </Button>
            </div>
            {discovery && (
              <div className="rounded-lg border border-zinc-200 bg-zinc-50/30 p-3 space-y-2 text-[11.5px]">
                {discovery.pipelines.map(p => (
                  <div key={p.id} className="space-y-0.5">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-zinc-900">{p.name}</span>
                      <code className="text-[10px] text-zinc-500 font-mono">{p.id}</code>
                    </div>
                    <div className="pl-3 text-[10.5px] text-zinc-500">
                      Stages: {p.stages.map(s => s.name).join(" · ")}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <p className="text-[10.5px] text-zinc-400 mt-1.5">Copy the desired Pipeline ID into your <code className="font-mono">GHL_PIPELINE_ID</code> env var to lock it in.</p>
          </section>

          <section>
            <div className="text-[10px] uppercase tracking-[0.12em] text-zinc-500 font-medium mb-2">Recent Sync Logs</div>
            {logs.length === 0 ? (
              <div className="text-[11.5px] text-zinc-400 italic px-3 py-3 border border-dashed border-zinc-200 rounded-lg">No sync runs yet.</div>
            ) : (
              <div className="space-y-1.5">
                {logs.map(l => (
                  <div key={l.id} className="rounded-lg border border-zinc-200 px-3 py-2 text-[11.5px]">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-zinc-900">{l.sync_started_at?.slice(0, 19)?.replace("T", " ")}</span>
                      <Badge variant="outline" className={`text-[10px] ${l.status === "success" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : l.status === "failed" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                        {l.status}
                      </Badge>
                    </div>
                    <div className="text-[10.5px] text-zinc-500 mt-1">
                      Fetched <span className="text-zinc-700">{l.records_fetched}</span> ·
                      Created <span className="text-zinc-700">{l.records_created}</span> ·
                      Updated <span className="text-zinc-700">{l.records_updated}</span> ·
                      Skipped <span className="text-zinc-700">{l.records_skipped}</span>
                    </div>
                    {l.error_message && (
                      <div className="mt-1.5 text-[10.5px] text-rose-700 bg-rose-50 border border-rose-100 rounded px-2 py-1 font-mono">
                        {l.error_message}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function ConfigRow({ k, v, ok }) {
  return (
    <div className="flex items-center justify-between px-3 py-2 gap-3">
      <span className="text-zinc-500 font-medium text-[11.5px]">{k}</span>
      <span className={`text-[11.5px] tabular-nums truncate text-right ${ok ? "text-zinc-900" : "text-rose-600"}`}>{v}</span>
    </div>
  );
}
